export type GroupingConfig = {
  groupByFromSettings: string[];
  valuesFromSettings: string[];
  percentileDistribution?: boolean;
  dataGranularitySource?: string;
  topNFromSettings?: string;
  rankByFromSettings?: string;
};

export type DateGroupingConfig = {
  groupByFromSettings: string | null;
  valuesFromSettings: string[];
  percentileDistribution?: boolean;
  dataGranularitySource?: string;
  rankByFromSettings?: string;
};

export const chartGroupingConfig: Record<string, GroupingConfig> = {
  stackedBarChart: {
    groupByFromSettings: ["decompositionMetric", "decompositionMetricSecondary"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  doughnutChart: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  pieChart: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  comboColumnLineChart: {
    groupByFromSettings: ["xAxis"],
    valuesFromSettings: ["yAxisPrimary", "yAxisSecondary"],
    topNFromSettings: "xAxis",
  },
  stackedColumnLineChart: {
    groupByFromSettings: ["xAxis"],
    valuesFromSettings: ["displayMetrics", "lineMetric"],
    topNFromSettings: "xAxis",
    rankByFromSettings: "lineMetric",
  },
  stackedColumnLineDecompositionChart: {
    groupByFromSettings: ["decompositionMetric", "decompositionMetricSecondary"],
    valuesFromSettings: ["displayMetric", "lineMetric"],
    topNFromSettings: "decompositionMetric",
    rankByFromSettings: "lineMetric",
  },
  stackedColumnLineTimeChart: {
    groupByFromSettings: ["xAxis"],
    valuesFromSettings: ["displayMetrics", "lineMetric"],
  },
  stackedBar100Chart: {
    groupByFromSettings: ["decompositionMetric", "decompositionMetricSecondary"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  stackedColumn100Chart: {
    groupByFromSettings: ["decompositionMetric", "decompositionMetricSecondary"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  barChart: {
    groupByFromSettings: ["yAxis"],
    valuesFromSettings: ["xAxisPrimary"],
    topNFromSettings: "yAxis",
  },
  boxPlot: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    percentileDistribution: true,
    topNFromSettings: "decompositionMetric",
  },
  boxPlotHorizontal: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    percentileDistribution: true,
    topNFromSettings: "decompositionMetric",
  },
  bubbleChart: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["xAxis", "yAxis", "displayMetric"],
    topNFromSettings: "decompositionMetric",
    rankByFromSettings: "displayMetric",
  },
  bulletChart: {
    groupByFromSettings: ["yAxis"],
    valuesFromSettings: ["yAxis"],
  },
  columnChart: {
    groupByFromSettings: ["xAxis"],
    valuesFromSettings: ["yAxisPrimary"],
    topNFromSettings: "xAxis",
  },
  waffleChart: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  treeMap: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  stackedColumnChart: {
    groupByFromSettings: ["decompositionMetric", "decompositionMetricSecondary"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  scatterPlot: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["xAxis", "yAxis"],
    topNFromSettings: "decompositionMetric",
  },
  packedCircleChart: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  nestedDoughnutChart: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric", "displayMetricSecondary"],
    topNFromSettings: "decompositionMetric",
  },
  lollipopChart: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    topNFromSettings: "decompositionMetric",
  },
  heatMap: {
    groupByFromSettings: ["decompositionMetric", "decompositionMetricSecondary"],
    valuesFromSettings: ["displayMetric"],
  },
  groupedBarChart: {
    groupByFromSettings: ["yAxis"],
    valuesFromSettings: ["xAxisPrimary", "xAxisSecondary"],
    topNFromSettings: "yAxis",
  },
  groupedColumnChart: {
    groupByFromSettings: ["xAxis"],
    valuesFromSettings: ["yAxisPrimary", "yAxisSecondary"],
    topNFromSettings: "xAxis",
  },
  funnelChart: {
    groupByFromSettings: [],
    valuesFromSettings: ["stage1Metric", "stage2Metric", "stage3Metric", "stage4Metric", "stage5Metric"],
  },
  dumbbellPlotHorizontal: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    percentileDistribution: true,
    topNFromSettings: "decompositionMetric",
  },
  dumbbellPlot: {
    groupByFromSettings: ["decompositionMetric"],
    valuesFromSettings: ["displayMetric"],
    percentileDistribution: true,
    topNFromSettings: "decompositionMetric",
  },
  comboBarLineChart: {
    groupByFromSettings: ["yAxis"],
    valuesFromSettings: ["xAxisPrimary", "xAxisSecondary"],
    topNFromSettings: "yAxis",
  },
};

export const timeChartGroupingConfig: Record<string, DateGroupingConfig> = {
  areaChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["yAxis"],
    dataGranularitySource: "xAxis",
  },
  barChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["xAxis"],
    dataGranularitySource: "yAxis",
  },
  comboBarLineChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["xAxis", "xAxisSecondary"],
    dataGranularitySource: "yAxis",
  },
  comboColumnLineChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["displayMetric", "displayMetricSecondary"],
    dataGranularitySource: "xAxis",
  },
  stackedColumnLineTimeChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["displayMetrics", "lineMetric"],
    dataGranularitySource: "xAxis",
  },
  stackedColumnLineDecompositionTimeChart: {
    groupByFromSettings: "decompositionMetricSecondary",
    valuesFromSettings: ["displayMetric", "lineMetric"],
    dataGranularitySource: "decompositionMetric",
    rankByFromSettings: "lineMetric",
  },
  columnChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["уAxis"],
    dataGranularitySource: "xAxis",
  },
  groupedBarChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["xAxisPrimary", "xAxisSecondary"],
    dataGranularitySource: "yAxis",
  },
  groupedColumnChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["уAxisPrimary", "уAxisSecondary"],
    dataGranularitySource: "xAxis",
  },
  lineChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["уAxisPrimary", "уAxisSecondary"],
    dataGranularitySource: "xAxis",
  },
  lineSeriesChart: {
    groupByFromSettings: "decompositionMetric",
    valuesFromSettings: ["yAxis"],
    dataGranularitySource: "xAxis",
    rankByFromSettings: "yAxis",
  },
  lollipopChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["yAxis"],
    dataGranularitySource: "decompositionMetric",
  },
  stackedArea100Chart: {
    groupByFromSettings: "decompositionMetricTop",
    valuesFromSettings: ["displayMetric"],
    dataGranularitySource: "decompositionMetric",
    rankByFromSettings: "displayMetric",
  },
  stackedAreaChart: {
    groupByFromSettings: "decompositionMetricTop",
    valuesFromSettings: ["displayMetric"],
    dataGranularitySource: "decompositionMetric",
    rankByFromSettings: "displayMetric",
  },
  stepLineChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["yAxisPrimary", "yAxisSecondary"],
    dataGranularitySource: "xAxis",
  },
  stepLineSeriesChart: {
    groupByFromSettings: "decompositionMetric",
    valuesFromSettings: ["yAxis"],
    dataGranularitySource: "xAxis",
    rankByFromSettings: "yAxis",
  },
  streamChart: {
    groupByFromSettings: "decompositionMetricTop",
    valuesFromSettings: [],
    dataGranularitySource: "decompositionMetric",
  },
  yearOverYearLineChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["yAxis"],
    dataGranularitySource: "xAxis",
  },
  yearOverYearStepLineChart: {
    groupByFromSettings: null,
    valuesFromSettings: ["yAxis"],
    dataGranularitySource: "xAxis",
  },
};
